<template>
    <div>
        <div class="bg-surface-50 justify-center flex w-full py-4">
            <!-- Breadcrumbs -->
            <div v-for="component in props.steps" :key="component.title" class="flex justify-stretch p-4 items-center">
                <div :class="{
                    'bg-grey-100 text-tertiary outline-offset-4 outline-primary-800 outline': component.id === currentStep.id & !component.disabledInBreadcrumbs,
                    'bg-grey-700 text-grey-600': component.id !== currentStep.id && component.disabledInBreadcrumbs,
                    'bg-grey-400 text-grey-50': component.position > currentStep.position & !component.disabledInBreadcrumbs,
                    'bg-grey-400 text-white': component.position < currentStep.position & !component.disabledInBreadcrumbs,
                }"
                    class="rounded-full p-3 min-w-[50px] w-[50px] h-[50px] flex text-center justify-center items-center text-3xl mr-2 transition-colors duration-500 ease-in-out">
                    {{ component.position + 1 }}
                </div>
                <div class="flex flex-col md-minus:hidden">
                    <div class="text-xs text-grey-400 h-4 translate-x-3" > {{ component.isOptional ? 'OPTIONAL' : '' }} </div>
                    <button class="text-xl pr-5" :disabled="isBreadcrumbDisabled(component)"
                        @click="changeStep(component.id)">
                        <div :class="{
                            'text-primary-600': component.position === currentStep.position & !component.disabledInBreadcrumbs,
                            'text-grey-400': component.position < currentStep.position & !component.disabledInBreadcrumbs,
                            'text-grey-600': component.position !== currentStep.position && component.disabledInBreadcrumbs,
                            'text-grey-400 ': component.position > currentStep.position & !component.disabledInBreadcrumbs
                        }" class="translate-x-3 break-keep font-semibold md-minus:text-lg lg-plus:text-2xl flex text-left items-start whitespace-nowrap transition-colors duration-500 ease-in-out">
                            {{ component.title }}
                        </div>
                    </button>
                    <div class="text-xs text-grey-600 translate-x-3" v-show="component.description?.length > 0 && component.id === currentStep.id & !component.disabledInBreadcrumbs">
                        {{ component.description }}
                    </div>
                </div>
                <hr v-if="component.position !== props.steps.length - 1"
                    class="ml-2 min-w-[45px] !border-grey-500 h-[2px] bg-grey-400 !border-2">
            </div>
        </div>
        <!-- Navigation -->
        <div class="bg-white">
            <div class="flex justify-end gap-4 text-2xl p-8 border-b border-grey-300">
                <Button v-if="currentStep.position !== props.steps[0].position" class="text-2xl mr-auto"
                    severity="primary_color" :icon="currentStep.iconPreviousStep" :disabled="isFirstStep"
                    :label="currentStep.labelPreviousStep" @click="changeStep('previous')" />

                <Button v-show="!isLastStep" class="text-2xl" :icon="currentStep.iconNextStep" iconPos="right" severity="primary_color"
                    :disabled="currentStep.hasNextStepCondition && !nextStepConditionsMet && !isCurrentStepCompleted"
                    :label="currentStep.labelNextStep" @click="changeStep('next')" />
            </div>
            <!-- Step Component -->
            <div class="p-8 flex justify-center">
                <KeepAlive>
                    <component :is="currentStep.component" v-bind="currentStep.props"
                        @update:nextStepConditionsTrue="updateNextStepConditionsMet" ref="currentStepRef" />
                </KeepAlive>
            </div>

        </div>
    </div>
</template>

<script setup>
import { onUnmounted, reactive, ref, computed, watch, onMounted, nextTick } from 'vue';
import { HProgressorStore } from './HProgressorStore.js';
import { EventBus } from '../../hermeneus.eventbus/eventbus.js';

// Variablen
const props = defineProps({
    steps: {
        type: Array,
        required: true
    }
});
const currentStep = ref();
const store = HProgressorStore();
const customEvents = ref([]);
const completedMandatorySteps = ref([]);
const currentStepRef = ref(null);
const nextStepConditionsMet = ref(false);

const defaultValues = {
    isOptional: true,
    hasNextStepCondition: false,
    disabledInBreadcrumbs: false,
    labelPreviousStep: 'Vorheriger Schritt',
    iconPreviousStep: 'fa-solid fa-caret-left',
    labelNextStep: 'Nächster Schritt',
    iconNextStep: 'fa-solid fa-caret-right',
    description: null
}

// Computed PropertyTraits mit Parametern
const isBreadcrumbDisabled = (component) => {
    return component.disabledInBreadcrumbs;
};

// Navigation-bezogene Computed PropertyTraits
const isFirstStep = computed(() => {
    return props.steps[0].position === currentStep.value.position;
});

const isLastStep = computed(() => {
    return props.steps[props.steps.length - 1].position === currentStep.value.position;
});

const isMandatoryStep = computed(() => {
    return currentStep.value.isOptional === false;
});

const isCurrentStepCompleted = computed(() => {
    return currentStep.value && completedMandatorySteps.value.includes(currentStep.value.id);
});

// Update-Funktion für nextStepConditionsMet
const updateNextStepConditionsMet = (value) => {
    nextStepConditionsMet.value = value || (currentStep.value && isCurrentStepCompleted.value);
};

// Überwachung der aktuellen Komponente für hasNextStepCondition
watch(currentStep, async () => {
    if (!currentStep.value) return;
    
    // Setze nextStepConditionsMet auf false bei jedem Schrittwechsel
    nextStepConditionsMet.value = isCurrentStepCompleted.value;

    // Warte auf das nächste Tick, um sicherzustellen, dass die Komponente gemounted ist
    await nextTick();

    // Prüfe, ob hasNextStepCondition für den aktuellen Schritt aktiviert ist
    if (currentStep.value.hasNextStepCondition) {
        // Prüfe, ob die Komponente die benötigte computed Property hat
        if (currentStepRef.value) {
            if (!('isInValidState' in currentStepRef.value)) {
                console.warn(`Die Komponente ${currentStep.value.component.name || 'unbekannt'} hat 'hasNextStepCondition' aktiviert, aber keine 'isInValidState' computed Property definiert. Die NextStepCondition muss stattdessen mit dem Event "configComplete" gesetzt werden.`);
            } else {
                // Initial den Wert von isInValidState abfragen
                nextStepConditionsMet.value = currentStepRef.value.isInValidState || (currentStep.value && isCurrentStepCompleted.value);

                // Überwachung des Wertes in der Komponente
                if (typeof currentStepRef.value.$watch === 'function') {
                    currentStepRef.value.$watch('isInValidState', (newValue) => {
                        nextStepConditionsMet.value = newValue || (currentStep.value && isCurrentStepCompleted.value);
                    });
                }
            }
        }
    }
}, { immediate: true });

// Überwachung von isCurrentStepCompleted
watch(isCurrentStepCompleted, (newValue) => {
    if (newValue) {
        nextStepConditionsMet.value = true;
    }
});

// Validierung
// Pflichtfelder müssen (korrekt) gesetzt sein (nicht undefined/null)
Object.values(props.steps).forEach(step => {
    Object.entries(step).forEach((key, value) => {
        if (['title', 'component', 'id', 'position'].includes(key) && value === null) {
            throw new TypeError('HProgressor: Die Eigenschaft ' + key + ' ist erforderlich.');
        }
        if (key === 'position') {
            if (isNaN(parseInt(value))) throw new TypeError('HProgressor: Die Eigenschaft position muss numerisch sein.');
            if (value < 0) throw new TypeError('HProgressor: Die Eigenschaft position muss positiv sein.');
        }
    })
});

// position-Werte müssen eindeutig sein
const allPositions = props.steps.map(step => step.position);
if (new Set(allPositions).size !== allPositions.length) {
    throw new Error('HProgressor: Die Eigenschaft position muss für jeden Step eindeutig sein.');
}

// Sortiere die Steps nach der Position (weil nicht garantiert werden kann, dass Nutzer die Objekte in der Reihenfolge der Positionen eingeben)
props.steps.sort((a, b) => a.position - b.position);

// Defaultwerte für optionale Eigenschaften setzen, sofern nicht angegeben; ID automatisch jedem Step als prop hinzufügen
props.steps.forEach((step, index) => {
    step.props = { ...step.props, id: step.id };
    props.steps[index] = { ...defaultValues, ...step }; // Merge für Defaultwerte nutzen: sofern Werte angegeben wurden, werden die Defaultwerte überschrieben
})

// Setze das Objekt mit der niedrigsten Position als Ausgangswert für currentStep
currentStep.value = props.steps[0];

// Steps/Komponenten im Store registrieren
const ids = props.steps.map(({ id }) => id);
store.registerComponentIDs(ids);


props.steps.forEach((value, index) => {
    if (value.customEvents && value.customEvents.length > 0) {
        value.customEvents.forEach((event) => { // Dient v.a. Kompatibilitätsgründen mit bestehenden Komponenten: EventListener für mögliche CustomEvents in den verwalteten Komponenten registrieren; die vom Event mitgebrachten Daten werden im Store gesammelt. Auf diese Weise gehen keine Daten verloren, die vorher direkt in Elternkomponenten der jetzt im Konfigurator verwalteten Komponenten abgefragt wurden.
            customEvents.value.push(event);
            if (index === 0) {
                EventBus.$on(event, (data) => {
                    store.setStepData(value.id, data)
                });
            }
        });
    }
});

EventBus.$on('activateStep', (stepID) => {
    findStepByAttribute('id', stepID).disabledInBreadcrumbs = false;
});


EventBus.$on('configComplete', (stepID) => {
    completedMandatorySteps.value.push(stepID);
})

function changeStep(actionOrStepID) {
    if (currentStep.value.customEvents && currentStep.value.customEvents.length > 0) {
        currentStep.value.customEvents.forEach((event) => {
            EventBus.$off(event);
        })
    }
    switch (actionOrStepID) {
        case 'next':
            currentStep.value = props.steps[props.steps.indexOf(findStepByAttribute('id', currentStep.value.id)) + 1];
            break;
        case 'previous':
            currentStep.value = props.steps[props.steps.indexOf(findStepByAttribute('id', currentStep.value.id)) - 1];
            break;
        default:
            if (!props.steps.some(step => step.id === actionOrStepID)) {
                throw new Error("Funktion changeStep() muss eine gültige Step-ID oder 'next' oder 'previous' als Parameter erhalten.");
            }
            currentStep.value = props.steps[props.steps.indexOf(findStepByAttribute('id', actionOrStepID))];
    }

    if (currentStep.value.customEvents && currentStep.value.customEvents.length > 0) {
        currentStep.value.customEvents.forEach((event) => {
            EventBus.$on(event, (data) => {
                store.setStepData(currentStep.value.id, data)
            });
        });
    }

}


function findStepByAttribute(attribute, value) {
    return props.steps.find(step => step[attribute] === value);
}


onUnmounted(() => {
    // Store für die nächste Verwendung der Komponente leeren
    // => Alle Daten, die im Rahmen der Konfig in den Store geschrieben wurden und später noch gebraucht werden, müssen vor dem Schließen des MultiStepConfigurators nach außen weitergegeben werden: z.B. mittels POST oder, falls der MultiStepConfigurator in einem Modal geöffnet wurde, indem das ModalCloseEvent erst in der Zielkomponente ausgelöst wird, nachdem die Daten aus dem Store gelesen wurden.
    // :FIXME: Console-Error: Store "HProgressorStore" is built using the setup syntax and does not implement $reset().
    
    store.$reset();
    customEvents.value.forEach((event) => {
        EventBus.$off(event);
    });
});

</script>

<style scoped></style>